import { useMutation } from '@apollo/client';
import { compatibility } from '@seek/ca-myprofile-utils';
import { metrics } from '@seek/metrics-js';
import { useLanguage, useTranslations } from '@vocab/react';
import { useEffect } from 'react';

import { browserLogError } from 'src/modules/error-logger/browserLog';
import { updateSubscriptionQL } from 'src/modules/graphql/notifications/preference.graphql';
import { useTypedSelector } from 'src/reducers';
import type {
  UpdateNotificationSubscriptionWithTokenMutation,
  UpdateNotificationSubscriptionWithTokenMutationVariables,
} from 'src/types/graphql';

import Subscription from '../common/subscription';
import PageLoader from '../page-loader';
import { trackUnsubscribeSuccessPageLoad } from '../tracking/tealium';

import translations from './.vocab';
import { UnsubscribeIcon } from './svg/unsubscribe-icon';
import UnsubscribeError from './unsubscribe-error';

const Unsubscribe = ({ token }: { token: string }) => {
  const { language: vocabLanguage } = useLanguage();
  const language =
    compatibility.getCompatibleLanguageFromLocaleString(vocabLanguage);

  const [unsubscribeMutation, { error, data }] = useMutation<
    UpdateNotificationSubscriptionWithTokenMutation,
    UpdateNotificationSubscriptionWithTokenMutationVariables
  >(updateSubscriptionQL);

  const locale = useTypedSelector(
    (state) => state.page.pageAnalytics.siteCountry,
  );
  const zone = useTypedSelector((store) => store.page.pageAnalytics.zone);
  const { t } = useTranslations(translations);

  useEffect(() => {
    if (token) {
      unsubscribeMutation({
        variables: {
          input: {
            token,
            subscribe: false,
            locale: `${language}-${locale.toUpperCase()}`,
          },
        },
      }).catch((e) => browserLogError(e));
    }
  }, [unsubscribeMutation, token, language, locale]);

  useEffect(() => {
    if (error) {
      metrics.count('unsubscribe-error-page-rendered');
    } else {
      metrics.count('unsubscribe-page-rendered');
    }
  }, [error]);

  if (error) {
    return <UnsubscribeError language={language} zone={zone} />;
  }

  if (data) {
    const result = data.updateNotificationSubscriptionWithToken;
    const product = result?.product.code;
    const channel = result?.subscriptions[0].channel.code; // always have 1
    const productName = result?.product.label.toLowerCase() ?? '';
    trackUnsubscribeSuccessPageLoad(product, channel);

    return (
      <Subscription
        title={t('default-catchall-heading', {
          PreferenceCentreEmailTitle: productName,
        })}
        cardTitle={t('unsubscribe-subscription-card-title')}
        body={t('default-catchall-message')}
        body2={t('default-catchall-message-2')}
        linkMessage={t('unsubscribe-subscription-link-message')}
        svg={<UnsubscribeIcon />}
      />
    );
  }

  return <PageLoader />;
};

export default Unsubscribe;
